import { createContext, useContext, useState, useCallback } from 'react'
import { GameState, WordLength } from '../types/game'
import { DEFAULT_MODE, DEFAULT_SETTINGS } from '../constants/gameConfig'
import { getGameSettings, setGameSettings } from '../utils/storage'

const defaultSettings: GameState = {
  selectedMode: DEFAULT_MODE,
  ...DEFAULT_SETTINGS,
}

interface GameSettingsContextType {
  settings: GameState
  updateSettings: (settings: Partial<GameState>) => void
  setMode: (mode: WordLength) => void
  toggleDarkMode: () => void
  toggleHighContrast: () => void
}

const GameSettingsContext = createContext<GameSettingsContextType>({
  settings: defaultSettings,
  updateSettings: () => {},
  setMode: () => {},
  toggleDarkMode: () => {},
  toggleHighContrast: () => {},
})

export const GameSettingsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [settings, setSettings] = useState<GameState>(() => {
    const savedSettings = getGameSettings()
    return savedSettings || defaultSettings
  })

  const updateSettings = useCallback(
    (
      newSettings:
        | Partial<GameState>
        | ((prev: GameState) => Partial<GameState>)
    ) => {
      setSettings((prev) => {
        const updated = {
          ...prev,
          ...(typeof newSettings === 'function'
            ? newSettings(prev)
            : newSettings),
        }
        setGameSettings(updated)
        return updated
      })
    },
    []
  )

  const setMode = useCallback(
    (mode: WordLength) => {
      updateSettings({ selectedMode: mode })
    },
    [updateSettings]
  )

  const toggleDarkMode = useCallback(() => {
    updateSettings((prev: GameState) => ({ ...prev, darkMode: !prev.darkMode }))
  }, [updateSettings])

  const toggleHighContrast = useCallback(() => {
    updateSettings((prev) => ({ ...prev, highContrast: !prev.highContrast }))
  }, [updateSettings])

  const value = {
    settings,
    updateSettings,
    setMode,
    toggleDarkMode,
    toggleHighContrast,
  }

  return (
    <GameSettingsContext.Provider value={value}>
      {children}
    </GameSettingsContext.Provider>
  )
}

export const useGameSettings = () => {
  const context = useContext(GameSettingsContext)
  if (!context) {
    throw new Error(
      'useGameSettings must be used within a GameSettingsProvider'
    )
  }
  return context
}
